import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { SchedulerLesson } from "../../models/schedule/lesson";
import { Duration } from "../../models/schedule/duration";
import { ISchedulerViewMode } from "../ui-lib/scheduler/ISchedulerViewMode";
import { patchComponentSchedule } from "../../helpers/utils/calendar.util";
import useSchedulerValidator from "./useSchedulerValidator";
import SchedulerGrid from "./SchedulerGrid";

export type SchedulerInputProps<T extends SchedulerLesson> = {
  lessons: T[][];
  startDate: Date;
  className?: string;
  setValue?: (lessons: T[][], changedLessons: T[]) => void;
  isReadOnly?: boolean;
  viewMode: ISchedulerViewMode<T>;
  duration?: Duration[];
};

const Scheduler = <T extends SchedulerLesson>({
  startDate,
  lessons,
  className,
  setValue = (_) => {},
  isReadOnly,
  viewMode,
  duration,
}: SchedulerInputProps<T>) => {
  const [page, setPage] = useState(0);

  const schedulerLessons = useMemo(
    () => viewMode.displayLessons(startDate, lessons, page),
    [lessons, page, startDate, viewMode],
  );

  const { isValid, hasNoActiveLessonsInDisplay } = useSchedulerValidator(
    lessons,
    schedulerLessons,
  );

  const toggleActive = (day: number, duration: number) => {
    const newLessons = viewMode.toggleActive(day, duration, lessons);
    setValue(
      patchComponentSchedule(lessons, newLessons) as any,
      newLessons as any,
    );
  };

  const toggleDay = (day: number, value: boolean) => {
    const newLessons = viewMode.setDay(day, value, lessons);
    setValue(patchComponentSchedule(lessons, newLessons), newLessons);
  };

  return (
    <div className={classNames(className, "flex flex-col")}>
      <div className="mx-auto flex items-center space-x-2">
        <button
          type="button"
          onClick={() => setPage((p) => p - 1)}
          className={classNames(
            "my-auto",
            !viewMode.hasPage(page - 1, lessons) && "invisible",
          )}
          data-cy="scheduler-back"
        >
          <ChevronLeftIcon className="h-10 w-10 text-th-primary-900"></ChevronLeftIcon>
        </button>
        {!!schedulerLessons[0] && !!schedulerLessons[0][0] && (
          <span className="font-bold text-lg">
            {schedulerLessons[0][0].date.toLocaleDateString()} -{" "}
            {schedulerLessons[
              schedulerLessons.length - 1
            ][0].date.toLocaleDateString()}
          </span>
        )}
        <button
          type="button"
          onClick={() => setPage((p) => p + 1)}
          className={classNames(
            "my-auto",
            !viewMode.hasPage(page + 1, lessons) && "invisible",
          )}
          data-cy="scheduler-next"
        >
          <ChevronRightIcon className="h-10 w-10 text-th-primary-900"></ChevronRightIcon>
        </button>
      </div>
      <div
        className={classNames("mx-auto mb-2", {
          hidden: isValid && !hasNoActiveLessonsInDisplay,
        })}
      >
        {!isValid && (
          <div
            className="text-sm font-bold text-red-600"
            data-cy="scheduler-error"
          >
            Mindestens eine Lektion muss selektiert sein
          </div>
        )}
        {hasNoActiveLessonsInDisplay && (
          <div
            className="text-sm font-bold text-yellow-500"
            data-cy="scheduler-warning"
          >
            {"Im angezeigten Zeitraum findet kein Unterricht statt"}
          </div>
        )}
      </div>
      <div className={classNames("flex space-x-1 overflow-auto")}>
        {duration && (
          <SchedulerGrid
            duration={duration}
            lessons={schedulerLessons}
            startDate={startDate}
            toggleActive={toggleActive}
            toggleDay={toggleDay}
            viewMode={viewMode}
            isReadOnly={isReadOnly}
          />
        )}
      </div>
    </div>
  );
};

export default Scheduler;
